import type { PreviewTestBootInfo, PreviewTestSession } from '@/features/runtime-test/buildPreviewTestSession'
import { normalizeAppRuntimeEnv } from '@/config/domainPresets'
import {
  installIndexedDbSaveProxy,
  uninstallIndexedDbSaveProxy,
} from '@/features/runtime-test/installIndexedDbSaveProxy'

export type PreviewEngineWindow = Window & typeof globalThis & {
  startEngine?: (info: PreviewTestBootInfo, canvas: HTMLCanvasElement, branch?: string) => void
  startLightLoaclTest?: (info: PreviewTestBootInfo, canvas: HTMLCanvasElement, branch?: string) => void
  /** `game.js` 全局退出：停止主循环、释放 tv/音频并 `clearBootContext` */
  exitGame?: () => boolean | void
  cgEngine?: {
    end?: () => void
  }
  bootContext?: {
    binSource: string
    memoryBoot: boolean
    memoryBins: Record<string, Uint8Array>
  }
  require?: (name: string) => unknown
}

export type PreviewTestEngineTarget = {
  runtimeWindow: PreviewEngineWindow
  runtimeDocument: Document
}

export interface PreviewTestEngineApi {
  startLightLoaclTest: (
    info: PreviewTestBootInfo,
    canvas: HTMLCanvasElement,
    requestRewriteMap?: Record<string, string>
  ) => void
  stopEngine: () => void
}

type WrappedSrcTarget = 'image' | 'media'
type PreviewTestFrameTarget = PreviewTestEngineTarget & {
  canvas: HTMLCanvasElement
}

const ENGINE_SCRIPT_URL = new URL('../../engine/game.js', import.meta.url).href
const ENGINE_GAME_INFO_DATA_URL = new URL('../../engine/GameInfoData.js', import.meta.url).href
const ENGINE_WEBGL_LIB_URL = new URL('../../engine/webGLLib.js', import.meta.url).href
const ENGINE_SAL_INTERFACE_URL = new URL('../../engine/SALInterface.js', import.meta.url).href
const ENGINE_SCRIPT_URLS = [
  ENGINE_GAME_INFO_DATA_URL,
  ENGINE_WEBGL_LIB_URL,
  ENGINE_SAL_INTERFACE_URL,
  ENGINE_SCRIPT_URL,
]
const PREVIEW_TEST_ENGINE_BRANCH = normalizeAppRuntimeEnv(import.meta.env.MODE || import.meta.env.VITE_APP_ENV)
const PREVIEW_TEST_ENGINE_BRANCH_PREFIX = PREVIEW_TEST_ENGINE_BRANCH.includes('test') ? 'test-' : ''

let engineLoadPromise: Promise<PreviewTestEngineApi> | null = null
let requestRewriteInstalled = false
let activeRequestRewriteMap: Record<string, string> = {}
let requireShimInstalled = false
let originalRequireDescriptor: PropertyDescriptor | null = null
let originalFetch: typeof window.fetch | null = null
let originalXhrOpen: typeof XMLHttpRequest.prototype.open | null = null
let originalSetAttribute: typeof Element.prototype.setAttribute | null = null
let originalImageSrcDescriptor: PropertyDescriptor | null = null
let originalMediaSrcDescriptor: PropertyDescriptor | null = null
let activeRuntimeWindow: PreviewEngineWindow | null = null
let activeRuntimeDocument: Document | null = null

const PREVIEW_TEST_ENGINE_SCRIPT_ATTR = 'data-preview-test-engine'
const PREVIEW_TEST_FRAME_CANVAS_ID = 'preview-runtime-canvas'
const PREVIEW_TEST_FRAME_HTML = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <style>
      html, body {
        margin: 0;
        width: 100%;
        height: 100%;
        overflow: hidden;
        background: #000000;
      }
      body {
        display: flex;
      }
      #${PREVIEW_TEST_FRAME_CANVAS_ID} {
        display: block;
        width: 100%;
        height: 100%;
        background: #000000;
      }
    </style>
  </head>
  <body>
    <canvas id="${PREVIEW_TEST_FRAME_CANVAS_ID}" aria-label="作品测试画面"></canvas>
    <script>
      document.addEventListener('dblclick', function (event) { event.preventDefault() }, { passive: false })
      document.addEventListener('gesturestart', function (event) { event.preventDefault() }, { passive: false })
      document.addEventListener('gesturechange', function (event) { event.preventDefault() }, { passive: false })
      document.addEventListener('gestureend', function (event) { event.preventDefault() }, { passive: false })
    </script>
  </body>
</html>`
const PREVIEW_TEST_GLOBAL_KEYS = [
  'start',
  'startEngine',
  'startLightLoaclTest',
  'cgEngine',
  'bootContext',
  'mallProxy',
  'serverAjax',
  'Configuration',
  'commonPlayer',
  'infoTool',
  'gameInfo',
  'OrgWeb',
]

function isEngineReady(runtimeWindow: PreviewEngineWindow): boolean {
  if (typeof runtimeWindow.startLightLoaclTest !== 'function') return false
  if (typeof runtimeWindow.eval !== 'function') return true
  try {
    return (
      String(runtimeWindow.eval('typeof mallProxy')) !== 'undefined'
      && String(runtimeWindow.eval('typeof serverAjax')) !== 'undefined'
      && String(runtimeWindow.eval('typeof Configuration')) !== 'undefined'
      && String(runtimeWindow.eval('typeof SAL_getHTTPProtocol')) === 'function'
    )
  } catch {
    return false
  }
}

function installEngineRequireShim(runtimeWindow: PreviewEngineWindow): void {
  if (typeof runtimeWindow.require === 'function') return

  originalRequireDescriptor = Object.getOwnPropertyDescriptor(runtimeWindow, 'require') ?? null
  Object.defineProperty(runtimeWindow, 'require', {
    configurable: true,
    writable: true,
    value: (name: string) => {
      switch (String(name || '').trim()) {
        case 'fs':
          return {
            existsSync: () => false,
            statSync: () => ({
              isFile: () => false,
            }),
          }
        case 'electron':
          return {
            ipcRenderer: {
              on: () => undefined,
              send: () => undefined,
            },
          }
        default:
          return {}
      }
    },
  })
  requireShimInstalled = true
}

function uninstallEngineRequireShim(runtimeWindow: PreviewEngineWindow | null): void {
  if (!requireShimInstalled || !runtimeWindow) return
  try {
    if (originalRequireDescriptor) {
      Object.defineProperty(runtimeWindow, 'require', originalRequireDescriptor)
    } else {
      delete (runtimeWindow as PreviewEngineWindow & { require?: unknown }).require
    }
  } catch {
    ;(runtimeWindow as PreviewEngineWindow & { require?: unknown }).require = undefined
  }
  originalRequireDescriptor = null
  requireShimInstalled = false
}

function extractMd5FromUrl(url: string): string {
  const matches = String(url || '').match(/[a-f0-9]{32}/gi) ?? []
  const last = matches[matches.length - 1] ?? ''
  return /^[a-f0-9]{32}$/i.test(last) ? last.toLowerCase() : ''
}

function rewriteRequestedUrl(value: string): string {
  const raw = String(value || '').trim()
  if (!raw) return raw
  const md5 = extractMd5FromUrl(raw)
  if (!md5) return raw
  return activeRequestRewriteMap[md5] || raw
}

function installRequestRewriteLayer(runtimeWindow: PreviewEngineWindow): void {
  if (requestRewriteInstalled) return
  requestRewriteInstalled = true

  originalFetch = runtimeWindow.fetch.bind(runtimeWindow)
  runtimeWindow.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    const RuntimeURL = runtimeWindow.URL
    const RuntimeRequest = runtimeWindow.Request
    if (typeof input === 'string') {
      return originalFetch!(rewriteRequestedUrl(input), init)
    }
    if (RuntimeURL && input instanceof RuntimeURL) {
      return originalFetch!(rewriteRequestedUrl(input.toString()), init)
    }
    if (RuntimeRequest && input instanceof RuntimeRequest) {
      return originalFetch!(new RuntimeRequest(rewriteRequestedUrl(input.url), input), init)
    }
    return originalFetch!(input, init)
  }) as typeof runtimeWindow.fetch

  originalXhrOpen = runtimeWindow.XMLHttpRequest.prototype.open
  runtimeWindow.XMLHttpRequest.prototype.open = function (
    method: string,
    url: string | URL,
    async?: boolean,
    username?: string | null,
    password?: string | null,
  ) {
    const rewritten = rewriteRequestedUrl(String(url))
    return originalXhrOpen!.call(this, method, rewritten, async ?? true, username, password)
  }

  const wrapSrcSetter = (proto: object, key: string, target: WrappedSrcTarget) => {
    const descriptor = Object.getOwnPropertyDescriptor(proto, key)
    if (!descriptor?.set) return
    if (target === 'image') {
      originalImageSrcDescriptor = descriptor
    } else {
      originalMediaSrcDescriptor = descriptor
    }
    Object.defineProperty(proto, key, {
      configurable: true,
      enumerable: descriptor.enumerable ?? true,
      get: descriptor.get
        ? function (this: unknown) {
            return descriptor.get!.call(this)
          }
        : undefined,
      set: function (this: unknown, value: string) {
        descriptor.set!.call(this, rewriteRequestedUrl(value))
      },
    })
  }

  if (typeof runtimeWindow.HTMLImageElement !== 'undefined') {
    wrapSrcSetter(runtimeWindow.HTMLImageElement.prototype, 'src', 'image')
  }
  if (typeof runtimeWindow.HTMLMediaElement !== 'undefined') {
    wrapSrcSetter(runtimeWindow.HTMLMediaElement.prototype, 'src', 'media')
  }

  originalSetAttribute = runtimeWindow.Element.prototype.setAttribute
  runtimeWindow.Element.prototype.setAttribute = function (qualifiedName: string, value: string) {
    if (String(qualifiedName || '').toLowerCase() === 'src') {
      return originalSetAttribute!.call(this, qualifiedName, rewriteRequestedUrl(value))
    }
    return originalSetAttribute!.call(this, qualifiedName, value)
  }
}

function uninstallRequestRewriteLayer(runtimeWindow: PreviewEngineWindow | null): void {
  if (!requestRewriteInstalled || !runtimeWindow) return
  if (originalFetch) {
    runtimeWindow.fetch = originalFetch
  }
  if (originalXhrOpen) {
    runtimeWindow.XMLHttpRequest.prototype.open = originalXhrOpen
  }
  if (typeof runtimeWindow.HTMLImageElement !== 'undefined' && originalImageSrcDescriptor) {
    Object.defineProperty(runtimeWindow.HTMLImageElement.prototype, 'src', originalImageSrcDescriptor)
  }
  if (typeof runtimeWindow.HTMLMediaElement !== 'undefined' && originalMediaSrcDescriptor) {
    Object.defineProperty(runtimeWindow.HTMLMediaElement.prototype, 'src', originalMediaSrcDescriptor)
  }
  if (originalSetAttribute) {
    runtimeWindow.Element.prototype.setAttribute = originalSetAttribute
  }
  originalFetch = null
  originalXhrOpen = null
  originalSetAttribute = null
  originalImageSrcDescriptor = null
  originalMediaSrcDescriptor = null
  requestRewriteInstalled = false
  activeRequestRewriteMap = {}
}

function removeLoadedEngineScripts(runtimeDocument: Document | null): void {
  if (!runtimeDocument) return
  runtimeDocument.querySelectorAll<HTMLScriptElement>(`script[${PREVIEW_TEST_ENGINE_SCRIPT_ATTR}="1"]`).forEach((node) => {
    node.remove()
  })
}

function resetPreviewTestGlobals(runtimeWindow: PreviewEngineWindow): void {
  if (runtimeWindow.bootContext) {
    runtimeWindow.bootContext.binSource = ''
    runtimeWindow.bootContext.memoryBoot = false
    runtimeWindow.bootContext.memoryBins = {}
  }
  const target = runtimeWindow as PreviewEngineWindow & Record<string, unknown>
  for (const key of PREVIEW_TEST_GLOBAL_KEYS) {
    try {
      target[key] = undefined
    } catch {
      /* ignore readonly globals */
    }
  }
}

function waitForPreviewTestFrameLoad(frame: HTMLIFrameElement): Promise<void> {
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      frame.removeEventListener('load', handleLoad)
      frame.removeEventListener('error', handleError)
    }
    const handleLoad = () => {
      cleanup()
      resolve()
    }
    const handleError = () => {
      cleanup()
      reject(new Error('测试 iframe 初始化失败'))
    }
    frame.addEventListener('load', handleLoad, { once: true })
    frame.addEventListener('error', handleError, { once: true })
    const supportsSrcdoc = 'srcdoc' in document.createElement('iframe')
    if (supportsSrcdoc) {
      frame.srcdoc = PREVIEW_TEST_FRAME_HTML
      return
    }
    frame.src = `data:text/html;charset=utf-8,${encodeURIComponent(PREVIEW_TEST_FRAME_HTML)}`
  })
}

async function ensurePreviewTestFrameTarget(frame: HTMLIFrameElement): Promise<PreviewTestFrameTarget> {
  await waitForPreviewTestFrameLoad(frame)
  const runtimeWindow = frame.contentWindow as PreviewEngineWindow | null
  const runtimeDocument = frame.contentDocument
  const canvas = runtimeDocument?.getElementById(PREVIEW_TEST_FRAME_CANVAS_ID)
  if (!runtimeWindow || !runtimeDocument || !(canvas instanceof runtimeWindow.HTMLCanvasElement)) {
    throw new Error('测试 iframe 画布初始化失败')
  }
  return {
    runtimeWindow,
    runtimeDocument,
    canvas,
  }
}

/**
 * 停止测试运行时：优先走引擎 `exitGame()` 完整退出，再补 `cgEngine.end()`（录制等场景）。
 * 须在清空 `bootContext` / 全局变量之前调用。
 */
function shutdownPreviewTestRuntime(runtimeWindow: PreviewEngineWindow): void {
  if (typeof runtimeWindow.exitGame === 'function') {
    try {
      runtimeWindow.exitGame()
    } catch (error) {
      console.warn('[preview-test] exitGame failed', error)
    }
  }
  try {
    runtimeWindow.cgEngine?.end?.()
  } catch {
    /* ignore runtime stop failure */
  }
}

function getEngineApi(runtimeWindow: PreviewEngineWindow): PreviewTestEngineApi {
  if (typeof runtimeWindow.startLightLoaclTest !== 'function') {
    throw new Error('测试引擎未正确挂载 startLightLoaclTest')
  }
  return {
    startLightLoaclTest(info, canvas, requestRewriteMap) {
      activeRequestRewriteMap = {}
      for (const [key, value] of Object.entries(requestRewriteMap ?? {})) {
        const md5 = extractMd5FromUrl(key) || String(key || '').trim().toLowerCase()
        if (!md5 || !value) continue
        activeRequestRewriteMap[md5] = value
      }
      if (!runtimeWindow.bootContext) {
        runtimeWindow.bootContext = {
          binSource: '',
          memoryBoot: false,
          memoryBins: {},
        }
      }
      installIndexedDbSaveProxy(runtimeWindow, { saveStorageKey: info.saveStorageKey })
      runtimeWindow.startLightLoaclTest?.(info, canvas, PREVIEW_TEST_ENGINE_BRANCH_PREFIX)
      installIndexedDbSaveProxy(runtimeWindow, { saveStorageKey: info.saveStorageKey })
    },
    stopEngine() {
      activeRequestRewriteMap = {}
      uninstallIndexedDbSaveProxy()
      shutdownPreviewTestRuntime(runtimeWindow)
    },
  }
}

export async function ensurePreviewTestEngineLoaded(target: PreviewTestEngineTarget): Promise<PreviewTestEngineApi> {
  const { runtimeWindow, runtimeDocument } = target
  activeRuntimeWindow = runtimeWindow
  activeRuntimeDocument = runtimeDocument

  if (isEngineReady(runtimeWindow)) {
    return getEngineApi(runtimeWindow)
  }
  if (engineLoadPromise) {
    return engineLoadPromise
  }

  installEngineRequireShim(runtimeWindow)
  installRequestRewriteLayer(runtimeWindow)
  engineLoadPromise = new Promise<PreviewTestEngineApi>((resolve, reject) => {
    const loadNext = (index: number) => {
      if (index >= ENGINE_SCRIPT_URLS.length) {
        try {
          if (!isEngineReady(runtimeWindow)) {
            throw new Error('测试引擎加载不完整')
          }
          resolve(getEngineApi(runtimeWindow))
        } catch (error) {
          engineLoadPromise = null
          reject(error)
        }
        return
      }

      const url = ENGINE_SCRIPT_URLS[index]!
      const script = runtimeDocument.createElement('script')
      script.src = url
      script.async = false
      script.dataset.previewTestEngine = '1'
      script.onload = () => {
        loadNext(index + 1)
      }
      script.onerror = () => {
        engineLoadPromise = null
        reject(new Error(`测试引擎依赖脚本加载失败：${url}`))
      }
      runtimeDocument.head.appendChild(script)
    }

    loadNext(0)
  })

  return engineLoadPromise
}

export async function startPreviewTestEngineInFrame(params: {
  frame: HTMLIFrameElement
  session: PreviewTestSession
}): Promise<void> {
  const target = await ensurePreviewTestFrameTarget(params.frame)
  const engine = await ensurePreviewTestEngineLoaded(target)
  engine.startLightLoaclTest(params.session.bootInfo, target.canvas, params.session.requestRewriteMap)
}

export function stopPreviewTestEngine(target?: PreviewTestEngineTarget): void {
  const runtimeWindow = target?.runtimeWindow ?? activeRuntimeWindow
  const runtimeDocument = target?.runtimeDocument ?? activeRuntimeDocument
  if (!runtimeWindow) {
    engineLoadPromise = null
    activeRuntimeWindow = null
    activeRuntimeDocument = null
    return
  }
  uninstallIndexedDbSaveProxy()
  if (isEngineReady(runtimeWindow)) {
    shutdownPreviewTestRuntime(runtimeWindow)
    activeRequestRewriteMap = {}
  } else {
    activeRequestRewriteMap = {}
  }
  removeLoadedEngineScripts(runtimeDocument ?? null)
  uninstallRequestRewriteLayer(runtimeWindow)
  uninstallEngineRequireShim(runtimeWindow)
  resetPreviewTestGlobals(runtimeWindow)
  engineLoadPromise = null
  activeRuntimeWindow = null
  activeRuntimeDocument = null
}

export function stopPreviewTestEngineInFrame(_frame?: HTMLIFrameElement | null): void {
  stopPreviewTestEngine()
}

/** 通知测试 iframe 内引擎随宿主尺寸变化（仅派发 resize，不重载脚本）。 */
export function notifyPreviewTestEngineResize(frame: HTMLIFrameElement | null | undefined): void {
  const runtimeWindow = frame?.contentWindow
  if (!runtimeWindow) return
  runtimeWindow.dispatchEvent(new Event('resize'))
}
